/**
 * Sentinel Dashboard Flag Hook
 * Tells the sidebar whether this account has the Sentinel workspace switched on.
 */

import { useAtomValue } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { sentinelWorkspaceService } from '@/services/sentinelWorkspace.service';
import { currentUserAtom, isAuthenticatedAtom } from '@/hooks/use-auth';
import { businessUnitsAtom, resolvedBusinessUnitIdAtom } from '@/hooks/use-business-units';
import { permissionsAtom } from '@/stores/permissions.store';
import { buildAbility } from '@/lib/ability';

const RBAC_ENABLED = import.meta.env.VITE_RBAC_ENABLED === 'true';

const sentinelSettingsQueryAtom = atomWithQuery((get) => {
  const perms = get(permissionsAtom);
  const user = get(currentUserAtom);
  const accountId = user?.accountId == null ? null : Number(user.accountId);
  // The endpoint needs the sentinel permission, so skip the call for anyone else.
  const canView =
    !RBAC_ENABLED || (!!perms && buildAbility(perms.permissions, perms.isSuperAdmin).can('view', 'sentinel-workspace'));

  // WAIT FOR THE BRAND, then read the RESOLVED one. This answer decides which dashboard
  // the Overview route renders, and it is per brand: an account can have Sentinel off while
  // one of its brands has it on. Asking before the brand is known sends no `bu_id`, gets
  // the ACCOUNT's answer, and paints Analytics v2 — then the brand arrives, the key
  // changes, and Brand Health replaces it under the operator.
  //
  // Both halves are needed. `isPending` holds the call until the brand list exists;
  // `resolvedBusinessUnitIdAtom` is what makes the brand a value rather than something an
  // effect writes a tick later, which is the window the gate alone did not close.
  //
  // `isPending` goes false for an account with no brands too, so a brandless account is
  // never left waiting — it resolves to no brand and asks account-wide, which is right.
  const brandSettled = !get(businessUnitsAtom).isPending;
  const buId = get(resolvedBusinessUnitIdAtom);

  return {
    // Keyed by account AND brand. The key is the only thing keeping two brands' answers
    // apart in the cache - a brand switch does not clear queries - so a key without the
    // brand serves the previous brand's gate to the next one.
    queryKey: ['sentinel', 'settings', accountId, buId ?? 'unresolved'],
    queryFn: () => sentinelWorkspaceService.getSettings(buId ? { bu_id: buId } : {}),
    enabled: get(isAuthenticatedAtom) && canView && accountId !== null && brandSettled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false
  };
});

export interface SentinelDashboardStatus {
  enabled: boolean | null;
  isLoading: boolean;
  isError: boolean;
}

export function useSentinelDashboardStatus(): SentinelDashboardStatus {
  const query = useAtomValue(sentinelSettingsQueryAtom);
  return {
    enabled: query.data?.dashboardEnabled ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

/** `null` until the flag is loaded, so existing navigation callers hide nothing before they know. */
export function useSentinelDashboardEnabled(): boolean | null {
  return useSentinelDashboardStatus().enabled;
}

export interface SentinelSettingsGate {
  state: 'loading' | 'ready' | 'error';
  /** Only meaningful once the state is 'ready'. */
  enabled: boolean;
  retry: () => void;
}

/** The workspace gate, from the call the sidebar has already cached. */
export function useSentinelSettingsGate(): SentinelSettingsGate {
  const query = useAtomValue(sentinelSettingsQueryAtom);
  const retry = () => void query.refetch();
  if (query.isError) return { state: 'error', enabled: false, retry };
  if (!query.data) return { state: 'loading', enabled: false, retry };
  return { state: 'ready', enabled: query.data.dashboardEnabled, retry };
}
